import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublishedSite() {
  const { subdomain } = useParams();
  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });

  useEffect(() => {
    loadSite();
  }, [subdomain]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error('Please enter your email');
      return;
    }
    // Simulate subscription (in real app, would call backend)
    toast.success('Thank you for subscribing!');
    setNewsletterEmail('');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    // Simulate contact form submission (in real app, would call backend)
    toast.success('Message sent! We\'ll get back to you soon.');
    setContactForm({ name: '', email: '', message: '' });
  };

  const loadSite = async () => {
    try {
      const response = await axios.get(`${API}/sites/${subdomain}`);
      if (response.data.success) {
        setSite(response.data.site);
        setPages(response.data.pages);
      }
    } catch (error) {
      console.error('Failed to load site:', error);
      setError('Website not found or not published');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading website...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const homePage = pages.find(p => p.isHome) || pages[0];

  return (
    <div className="min-h-screen bg-white">
      {/* Render the home page sections */}
      {homePage?.sections?.map((section, index) => (
        <div key={section.id || index}>
          {section.type === 'hero' && (
            <div
              id={section.id}
              className="relative min-h-[500px] flex items-center justify-center text-white"
              style={{
                backgroundImage: section.content.backgroundImage
                  ? `url(${section.content.backgroundImage})`
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-black/40"></div>
              <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  {section.content.heading}
                </h1>
                <p className="text-xl md:text-2xl mb-8 text-gray-100">
                  {section.content.subheading}
                </p>
                {section.content.buttonText && (
                  <button
                    onClick={() => {
                      const link = section.content.buttonLink || '#';
                      if (link.startsWith('#')) {
                        scrollToSection(link.substring(1));
                      } else {
                        window.location.href = link;
                      }
                    }}
                    className="inline-block px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors cursor-pointer"
                  >
                    {section.content.buttonText}
                  </button>
                )}
              </div>
            </div>
          )}

          {section.type === 'about' && (
            <div id={section.id} className="py-20 px-4">
              <div className="max-w-6xl mx-auto">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div>
                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                      {section.content.heading}
                    </h2>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      {section.content.text}
                    </p>
                  </div>
                  {section.content.image && (
                    <div className="rounded-lg overflow-hidden shadow-xl">
                      <img
                        src={section.content.image}
                        alt={section.content.heading}
                        className="w-full h-auto"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {section.type === 'services' && (
            <div id={section.id} className="py-20 px-4 bg-gray-50">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">
                  {section.content.heading}
                </h2>
                <div className="grid md:grid-cols-3 gap-8">
                  {section.content.items?.map((item, i) => (
                    <div
                      key={i}
                      className="bg-white p-8 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow"
                    >
                      <div className="text-5xl mb-4">{item.icon}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section.type === 'contact' && (
            <div id={section.id} className="py-20 px-4 bg-gray-900 text-white">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-4xl font-bold text-center mb-12">
                  {section.content.heading}
                </h2>
                
                {/* Contact Info */}
                <div className="grid md:grid-cols-3 gap-8 mb-12">
                  <div className="text-center">
                    <div className="text-3xl mb-3">📧</div>
                    <p className="text-lg">{section.content.email}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-3">📞</div>
                    <p className="text-lg">{section.content.phone}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-3">📍</div>
                    <p className="text-lg">{section.content.address}</p>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="max-w-2xl mx-auto bg-gray-800 p-8 rounded-lg">
                  <h3 className="text-2xl font-bold mb-6">Send us a message</h3>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder="Your Name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg text-gray-900"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder="Your Email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg text-gray-900"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        placeholder="Your Message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg text-gray-900"
                        rows="4"
                        required
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                    >
                      Send Message
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {section.type === 'gallery' && (
            <div id={section.id} className="py-20 px-4 bg-white">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl font-bold text-center mb-12">{section.content.heading}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {section.content.images?.map((img, i) => (
                    <div key={i} className="aspect-square overflow-hidden rounded-lg shadow-lg">
                      <img src={img.url} alt={img.alt} className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section.type === 'testimonials' && (
            <div id={section.id} className="py-20 px-4 bg-gray-50">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl font-bold text-center mb-12">{section.content.heading}</h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {section.content.testimonials?.map((testimonial, i) => (
                    <div key={i} className="bg-white p-8 rounded-lg shadow-lg">
                      <div className="flex gap-1 mb-4">
                        {[...Array(testimonial.rating || 5)].map((_, j) => (
                          <span key={j} className="text-yellow-400">★</span>
                        ))}
                      </div>
                      <p className="text-gray-700 text-lg mb-4">"{testimonial.text}"</p>
                      <div>
                        <p className="font-bold">{testimonial.author}</p>
                        <p className="text-sm text-gray-500">{testimonial.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section.type === 'features' && (
            <div id={section.id} className="py-20 px-4 bg-white">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl font-bold text-center mb-4">{section.content.heading}</h2>
                {section.content.subheading && (
                  <p className="text-xl text-center text-gray-600 mb-12">{section.content.subheading}</p>
                )}
                <div className="grid md:grid-cols-3 gap-8">
                  {section.content.features?.map((feature, i) => (
                    <div key={i} className="text-center p-6 hover:shadow-xl transition-shadow rounded-lg">
                      <div className="text-5xl mb-4">{feature.icon}</div>
                      <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section.type === 'pricing' && (
            <div id={section.id} className="py-20 px-4 bg-gray-50">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl font-bold text-center mb-4">{section.content.heading}</h2>
                {section.content.subheading && (
                  <p className="text-xl text-center text-gray-600 mb-12">{section.content.subheading}</p>
                )}
                <div className="grid md:grid-cols-3 gap-8">
                  {section.content.plans?.map((plan, i) => (
                    <div key={i} className={`bg-white rounded-lg shadow-lg p-8 ${plan.highlighted ? 'ring-4 ring-blue-500 transform scale-105' : ''}`}>
                      <h3 className="text-2xl font-bold text-center mb-4">{plan.name}</h3>
                      <div className="text-center mb-6">
                        <span className="text-5xl font-bold">${plan.price}</span>
                        <p className="text-gray-500 mt-2">{plan.period}</p>
                      </div>
                      <ul className="space-y-3 mb-8">
                        {plan.features?.map((feature, j) => (
                          <li key={j} className="flex items-center">
                            <span className="text-green-500 mr-2">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <button 
                        onClick={() => {
                          // Scroll to contact section or show modal
                          const contactSection = homePage?.sections?.find(s => s.type === 'contact');
                          if (contactSection) {
                            scrollToSection(contactSection.id);
                            toast.success(`Selected ${plan.name} plan! Contact us below to get started.`);
                          } else {
                            toast.info(`You selected the ${plan.name} plan!`);
                          }
                        }}
                        className={`w-full py-3 rounded-lg font-semibold transition-colors cursor-pointer ${plan.highlighted ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                      >
                        {plan.buttonText}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section.type === 'team' && (
            <div id={section.id} className="py-20 px-4 bg-white">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-4xl font-bold text-center mb-4">{section.content.heading}</h2>
                {section.content.subheading && (
                  <p className="text-xl text-center text-gray-600 mb-12">{section.content.subheading}</p>
                )}
                <div className="grid md:grid-cols-4 gap-8">
                  {section.content.members?.map((member, i) => (
                    <div key={i} className="text-center">
                      <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gray-200 overflow-hidden">
                        {member.image && <img src={member.image} alt={member.name} className="w-full h-full object-cover" />}
                      </div>
                      <h3 className="font-bold text-lg">{member.name}</h3>
                      <p className="text-sm text-gray-500">{member.role}</p>
                      {member.bio && <p className="text-sm text-gray-600 mt-2">{member.bio}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section.type === 'faq' && (
            <div id={section.id} className="py-20 px-4 bg-gray-50">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-4xl font-bold text-center mb-4">{section.content.heading}</h2>
                {section.content.subheading && (
                  <p className="text-xl text-center text-gray-600 mb-12">{section.content.subheading}</p>
                )}
                <div className="space-y-4">
                  {section.content.faqs?.map((faq, i) => (
                    <div key={i} className="bg-white rounded-lg p-6 shadow">
                      <h3 className="font-bold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section.type === 'newsletter' && (
            <div id={section.id} className="py-20 px-4" style={{ backgroundColor: section.content.backgroundColor || '#1e40af' }}>
              <div className="max-w-2xl mx-auto text-center" style={{ color: section.content.textColor || '#ffffff' }}>
                <h2 className="text-4xl font-bold mb-4">{section.content.heading}</h2>
                <p className="text-xl mb-8">{section.content.subheading}</p>
                <form onSubmit={handleNewsletterSubmit} className="flex gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder={section.content.placeholder || 'Enter your email'}
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-lg text-gray-900"
                    required
                  />
                  <button type="submit" className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                    {section.content.buttonText}
                  </button>
                </form>
              </div>
            </div>
          )}

          {section.type === 'video' && (
            <div id={section.id} className="py-20 px-4 bg-gray-900">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-4xl font-bold text-white mb-4">{section.content.heading}</h2>
                {section.content.subheading && (
                  <p className="text-xl text-gray-300 mb-8">{section.content.subheading}</p>
                )}
                <div className="aspect-video rounded-lg overflow-hidden shadow-2xl">
                  <iframe
                    src={section.content.videoUrl}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          )}

          {section.type === 'stats' && (
            <div id={section.id} className="py-20 px-4" style={{ backgroundColor: section.content.backgroundColor || '#1f2937' }}>
              <div className="max-w-6xl mx-auto" style={{ color: section.content.textColor || '#ffffff' }}>
                <h2 className="text-4xl font-bold text-center mb-4">{section.content.heading}</h2>
                {section.content.subheading && (
                  <p className="text-xl text-center mb-12 opacity-80">{section.content.subheading}</p>
                )}
                <div className="grid md:grid-cols-4 gap-8">
                  {section.content.stats?.map((stat, i) => (
                    <div key={i} className="text-center">
                      <div className="text-5xl font-bold mb-2 text-blue-400">{stat.number}</div>
                      <p className="text-lg">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8 text-center border-t border-gray-800">
        <p className="text-gray-400">
          © {new Date().getFullYear()} {site?.siteName}. All rights reserved.
        </p>
      </div>
    </div>
  );
}
